import React, { useState, useEffect } from 'react'
import { Globe, TrendingUp, Users, Target, AlertCircle, Wifi, Signal } from 'lucide-react'
import LoadingSpinner from '../ui/LoadingSpinner'
import ErrorMessage from '../ui/ErrorMessage'
import MetricCard from '../ui/MetricCard'
import DataTable from '../ui/DataTable'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ScatterChart, Scatter, PieChart, Pie, Cell } from 'recharts'
import { apiService } from '../../services/api'
import { debug } from '../../utils/debug'
import { useUser } from '../../context/UserContext'

const NetworkCoverage = () => {
  const { hasOperatorAccess } = useUser()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [coverageData, setCoverageData] = useState([])
  const [rules, setRules] = useState(null)
  
  // Filters
  const [daysBack, setDaysBack] = useState(30)

  debug.component('NetworkCoverage', 'Component initialized')

  useEffect(() => {
    fetchData()
  }, [daysBack])

  const fetchData = async () => {
    setLoading(true)
    setError(null)
    
    try {
      debug.log('Fetching network coverage data', { daysBack }, 'NetworkCoverage')
      
      const [coverageResponse, rulesResponse] = await Promise.all([
        apiService.getNetworkCoverage({ days_back: daysBack }),
        apiService.getRules()
      ])
      
      const data = coverageResponse.data
      const rulesData = rulesResponse.data || { operators: {} }
      
      console.log('=== NETWORK COVERAGE FRONTEND DEBUG ===')
      console.log('Raw API response:', data)
      data.forEach(op => {
        console.log(`${op.operator_name}: kams_visiting=${op.kams_visiting}, coverage_rate=${op.coverage_rate}, status=${op.coverage_status}`)
      })
      console.log('=== END DEBUG ===')
      
      debug.log('Network coverage data fetched successfully', data, 'NetworkCoverage')
      setRules(rulesData)
      
      // Modify data based on rules.json
      const modifiedData = data.map(operator => {
        const operatorCode = getOperatorCodeFromName(operator.operator_name)
        const operatorConfig = rulesData.operators?.[operatorCode]
        
        let modifiedOperator = { ...operator }
        
        // Override total_salons for TMPL and P4 based on rules.json
        if (operatorConfig?.total_salons) {
          modifiedOperator.total_salons_in_network = operatorConfig.total_salons
          // Recalculate coverage rate if needed
          if (operator.visited_salons && operatorConfig.total_salons > 0) {
            // Keep as number, not string (parseFloat to ensure it's a number)
            modifiedOperator.coverage_rate = parseFloat(((operator.visited_salons / operatorConfig.total_salons) * 100).toFixed(1))
          }
        }
        
        return modifiedOperator
      })
      
      // Filter data based on user's operator access
      const filteredData = modifiedData.filter(operator => {
        const operatorCode = getOperatorCodeFromName(operator.operator_name)
        // Map codes to standard format
        const codeMap = {
          'OPL': 'ORANGE',
          'TMPL': 'TMOBILE',
          'PLK': 'PLUS',
          'P4': 'P4'
        }
        const standardCode = codeMap[operatorCode] || operatorCode
        return hasOperatorAccess(standardCode)
      })
      
      setCoverageData(filteredData || [])
      
    } catch (err) {
      debug.error('Error fetching network coverage data', err, 'NetworkCoverage')
      setError('Błąd podczas ładowania danych pokrycia sieci')
    } finally {
      setLoading(false)
    }
  }

  const getOperatorCodeFromName = (operatorName) => {
    const nameToCode = {
      'Orange': 'OPL',
      'T-Mobile': 'TMPL',
      'Plus': 'PLK',
      'Play': 'P4'
    }
    return nameToCode[operatorName] || operatorName
  }

  const getSalonsLabel = (operator) => {
    const operatorCode = getOperatorCodeFromName(operator.operator_name)
    const operatorConfig = rules?.operators?.[operatorCode]
    const isSpecialAttentionOnly = operatorConfig?.coverage_only_for_special_attention === true
    
    if (isSpecialAttentionOnly) {
      return `${operator.visited_salons}/${operator.total_salons_in_network} salonów (spec. uwaga)`
    }
    return `${operator.visited_salons}/${operator.total_salons_in_network} salonów`
  }

  const getOperatorColor = (operatorName) => {
    const colors = {
      'Orange': '#FF7900',
      'Plus': '#00A651', 
      'T-Mobile': '#E20074',
      'Play': '#9333EA'
    }
    
    // Match by name containing operator
    for (const [op, color] of Object.entries(colors)) {
      if (operatorName.toLowerCase().includes(op.toLowerCase())) {
        return color
      }
    }
    
    return '#6b7280' // Default gray
  }

  const getCoverageStatusColor = (status) => {
    const colors = {
      'DOSKONAŁY': '#10b981', // Green
      'DOBRY': '#3b82f6',     // Blue  
      'ŚREDNI': '#f59e0b',    // Yellow
      'SŁABY': '#ef4444'      // Red
    }
    return colors[status] || '#6b7280' // Default gray
  }

  const getCoverageStatusEmoji = (status) => {
    const emojis = {
      'DOSKONAŁY': '🎯',
      'DOBRY': '✅', 
      'ŚREDNI': '⚠️',
      'SŁABY': '❌'
    }
    return emojis[status] || '❓'
  }

  const renderRetryButton = () => (
    <button
      onClick={fetchData}
      className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
    >
      Spróbuj ponownie
    </button>
  )

  if (loading) {
    return <LoadingSpinner message="Ładowanie danych pokrycia sieci..." />
  }

  if (error) {
    return (
      <ErrorMessage 
        message={error}
        onRetry={fetchData}
      />
    )
  }

  // Calculate overview metrics
  const totalOperators = coverageData.length
  const totalSalons = coverageData.reduce((sum, item) => sum + (item.total_salons_in_network || 0), 0)
  const totalVisited = coverageData.reduce((sum, item) => sum + (item.visited_salons || 0), 0)
  const avgCoverage = coverageData.length > 0 ? 
    coverageData.filter(item => item.kams_visiting && item.coverage_rate !== null)
             .reduce((sum, item) => sum + (item.coverage_rate || 0), 0) / 
    Math.max(1, coverageData.filter(item => item.kams_visiting).length) : 0

  // Sort data by coverage rate descending (kams_visiting first, then by rate)
  const sortedData = [...coverageData].sort((a, b) => {
    // Non-visiting operators go to the end
    if (!a.kams_visiting && b.kams_visiting) return 1
    if (a.kams_visiting && !b.kams_visiting) return -1
    if (!a.kams_visiting && !b.kams_visiting) return 0
    
    // Both have kams_visiting, sort by coverage_rate
    return (b.coverage_rate || 0) - (a.coverage_rate || 0)
  })

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center">
            <Globe className="h-6 w-6 mr-2 text-blue-600" />
            🌐 Pokrycie Sieci per Operator
          </h2>
          <p className="text-gray-600 mt-1">
            Procent salonów każdej sieci odwiedzonych przez wszystkich KAMów
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 p-4 bg-gray-50 rounded-lg">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Okres analizy</label>
          <select
            value={daysBack}
            onChange={(e) => setDaysBack(Number(e.target.value))}
            className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
          >
            <option value={7}>7 dni</option>
            <option value={14}>14 dni</option>
            <option value={30}>30 dni</option>
            <option value={60}>60 dni</option>
            <option value={90}>90 dni</option>
          </select>
        </div>
      </div>

      {!coverageData.length ? (
        <div className="text-center py-8">
          <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500 mb-2">Brak danych pokrycia sieci do wyświetlenia.</p>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 max-w-2xl mx-auto">
            <p className="text-blue-800 text-sm">
              💡 <strong>Uwaga:</strong> Endpoint <code>/network/coverage</code> używa nowej logiki - 
              pokazuje pokrycie sieci per operator, nie cross-operator coverage per KAM.
            </p>
          </div>
          {renderRetryButton()}
        </div>
      ) : (
        <>
          {/* Overview Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <MetricCard
              title="Sieci Aktywne"
              value={totalOperators}
              icon={Wifi}
            />
            <MetricCard
              title="Salony Ogółem"
              value={totalSalons.toLocaleString()}
              icon={Target}
            />
            <MetricCard
              title="Salony Odwiedzone"
              value={totalVisited.toLocaleString()}
              icon={Users}
            />
            <MetricCard
              title="Średnie Pokrycie"
              value={`${avgCoverage.toFixed(1)}%`}
              icon={Signal}
            />
          </div>

          {/* Operator Coverage Cards */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">🎯 Pokrycie per Operator</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
              {sortedData.slice(0, 4).map((operator, index) => {
                const statusEmoji = getCoverageStatusEmoji(operator.coverage_status)
                const operatorColor = getOperatorColor(operator.operator_name)
                const statusColor = getCoverageStatusColor(operator.coverage_status)
                
                return (
                  <div 
                    key={index}
                    className="border-2 rounded-lg p-4 text-center bg-gray-50"
                    style={{ borderColor: operatorColor }}
                  >
                    <h4 
                      className="font-semibold text-lg mb-2"
                      style={{ color: operatorColor }}
                    >
                      {operator.operator_name}
                    </h4>
                    <div className="text-4xl mb-2">
                      {statusEmoji}
                    </div>
                    <div 
                      className="text-2xl font-bold mb-1"
                      style={{ color: statusColor }}
                    >
                      {operator.kams_visiting && operator.coverage_rate !== null && typeof operator.coverage_rate === 'number'
                        ? `${operator.coverage_rate.toFixed(1)}%` 
                        : 'N/A'
                      }
                    </div>
                    <div className="text-gray-600 text-sm mb-2">
                      {getSalonsLabel(operator)}
                    </div>
                    <div className="text-gray-500 text-xs">
                      {operator.active_kams_count} KAMów • {operator.last_30d_visits} wizyt
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          {/* Detailed Coverage Table */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">🔍 Szczegółowe Dane Pokrycia</h3>
            
            <DataTable
              rows={sortedData.map((item, index) => ({
                id: index,
                operator_name: item.operator_name || '',
                total_salons_in_network: Number(item.total_salons_in_network) || 0,
                visited_salons: Number(item.visited_salons) || 0,
                coverage_rate: item.kams_visiting && item.coverage_rate !== null ? Number(item.coverage_rate) : 'N/A',
                coverage_status: item.coverage_status || '',
                active_kams_count: Number(item.active_kams_count) || 0,
                last_30d_visits: Number(item.last_30d_visits) || 0,
              }))}
              columns={[
                {
                  field: 'operator_name',
                  headerName: 'Operator',
                  flex: 2,
                  minWidth: 120,
                  type: 'string',
                },
                {
                  field: 'total_salons_in_network',
                  headerName: 'Salony w Sieci',
                  flex: 1.5,
                  minWidth: 120,
                  type: 'number',
                },
                {
                  field: 'visited_salons',
                  headerName: 'Salony Odwiedzone',
                  flex: 1.5,
                  minWidth: 130,
                  type: 'number',
                },
                {
                  field: 'coverage_rate',
                  headerName: 'Pokrycie (%)',
                  flex: 1.2,
                  minWidth: 110,
                  type: 'number',
                  renderCell: (params) => (
                    <span>
                      {typeof params.value === 'number' ? `${params.value.toFixed(1)}%` : params.value}
                    </span>
                  ),
                },
                {
                  field: 'coverage_status',
                  headerName: 'Status',
                  flex: 1.3,
                  minWidth: 110,
                  type: 'string',
                  renderCell: (params) => (
                    <span>
                      {getCoverageStatusEmoji(params.value)} {params.value}
                    </span>
                  ),
                },
                {
                  field: 'active_kams_count',
                  headerName: 'Aktywni KAMowie',
                  flex: 1.3,
                  minWidth: 120,
                  type: 'number',
                },
                {
                  field: 'last_30d_visits',
                  headerName: 'Wizyty (30d)',
                  flex: 1.2,
                  minWidth: 110,
                  type: 'number',
                },
              ]}
              pageSize={10}
              autoHeight
              sortModel={[{ field: 'coverage_rate', sort: 'desc' }]}
            />
          </div>

          {/* Coverage Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Coverage per Operator Bar Chart */}
            <div className="bg-white p-4 rounded-lg shadow">
              <h4 className="text-md font-semibold text-gray-900 mb-4">Pokrycie per Operator</h4>
              {sortedData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={sortedData.filter(item => item.kams_visiting)}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="operator_name" 
                      angle={-45}
                      textAnchor="end"
                      height={80}
                    />
                    <YAxis />
                    <Tooltip formatter={(value) => [typeof value === 'number' ? `${value.toFixed(1)}%` : 'N/A', 'Pokrycie']} />
                    <Bar 
                      dataKey="coverage_rate" 
                      fill="#3b82f6"
                      name="Pokrycie (%)"
                    />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-gray-500 text-center py-8">Brak danych do wykresu pokrycia.</p>
              )}
            </div>

            {/* Coverage Status Distribution */}
            <div className="bg-white p-4 rounded-lg shadow">
              <h4 className="text-md font-semibold text-gray-900 mb-4">Rozkład Statusów Pokrycia</h4>
              {sortedData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={Object.entries(
                        sortedData.reduce((acc, item) => {
                          const status = item.coverage_status || 'NIEZNANY'
                          acc[status] = (acc[status] || 0) + 1
                          return acc
                        }, {})
                      ).map(([status, count]) => ({ 
                        name: status, 
                        value: count,
                        fill: getCoverageStatusColor(status)
                      }))}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {Object.entries(
                        sortedData.reduce((acc, item) => {
                          const status = item.coverage_status || 'NIEZNANY'
                          acc[status] = (acc[status] || 0) + 1
                          return acc
                        }, {})
                      ).map(([status], index) => (
                        <Cell key={`cell-${index}`} fill={getCoverageStatusColor(status)} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-gray-500 text-center py-8">Brak danych do wykresu statusów.</p>
              )}
            </div>
          </div>

          {/* Coverage vs Network Size Scatter */}
          <div className="bg-white p-4 rounded-lg shadow">
            <h4 className="text-md font-semibold text-gray-900 mb-4">Pokrycie vs Wielkość Sieci</h4>
            {sortedData.length > 0 ? (
              <ResponsiveContainer width="100%" height={400}>
                <ScatterChart data={sortedData.filter(item => item.kams_visiting)}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    type="number" 
                    dataKey="total_salons_in_network" 
                    name="Salony w Sieci"
                    label={{ value: 'Salony w Sieci', position: 'insideBottom', offset: -5 }}
                  />
                  <YAxis 
                    type="number" 
                    dataKey="coverage_rate" 
                    name="Pokrycie"
                    label={{ value: 'Pokrycie (%)', angle: -90, position: 'insideLeft' }}
                  />
                  <Tooltip 
                    cursor={{ strokeDasharray: '3 3' }}
                    formatter={(value, name) => [
                      name === 'coverage_rate' ? (typeof value === 'number' ? `${value.toFixed(1)}%` : 'N/A') : value,
                      name === 'coverage_rate' ? 'Pokrycie' : 'Salony w Sieci'
                    ]}
                    labelFormatter={(label, payload) => {
                      if (payload && payload.length > 0) {
                        return payload[0].payload.operator_name
                      }
                      return label
                    }}
                  />
                  <Scatter 
                    name="Operatorzy" 
                    dataKey="coverage_rate" 
                    fill="#3b82f6"
                  />
                </ScatterChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-gray-500 text-center py-8">Brak danych do wykresu scatter.</p>
            )}
            <p className="text-gray-500 text-sm mt-2 text-center">
              Opis: Większe sieci (więcej salonów) vs ich pokrycie wizytami
            </p>
          </div>
        </>
      )}
    </div>
  )
}

export default NetworkCoverage